// src/pages/AboutPage.js
import React from 'react';
import { Box, Typography, Button, Avatar, IconButton } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import LanguageIcon from '@mui/icons-material/Language';
import SyncIcon from '@mui/icons-material/Sync';
import CloudIcon from '@mui/icons-material/Cloud';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import Breadcrumbs from '../components/common/Breadcrumbs';

const BREADCRUMBS = [{ name: 'About', url: 'https://dublabs.ai/about-us' }];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

const TECH_PILLARS = [
  { Icon: RecordVoiceOverIcon, title: 'Advanced Voice Cloning', body: 'Preserves speaker characteristics, tone, and emotion across every language.' },
  { Icon: LanguageIcon, title: 'AI-Powered Translation', body: 'Maintains cultural context, idioms, and emotional nuance — not just words.' },
  { Icon: SyncIcon, title: 'Precise Lip-Sync', body: 'Frame-level sync ensures dubbed video looks completely natural on screen.' },
  { Icon: CloudIcon, title: 'White-label Architecture', body: 'Seamless brand integration with custom API and full white-label options.' },
];

const WHO_WE_SERVE = [
  { title: 'Media Houses', body: 'Broadcasters and studios scaling localisation across hundreds of hours of content.' },
  { title: 'Marketing Agencies', body: 'Production teams delivering multilingual campaigns without ballooning budgets.' },
  { title: 'Content Creators', body: 'YouTubers and influencers expanding their audience reach across language barriers.' },
  { title: 'Enterprise', body: 'Global organisations training, communicating, and storytelling in every market.' },
];

const WHY_POINTS = [
  'Minutes to complete, not weeks',
  'Voice preserved across every language',
  'Perfect lip-sync that looks natural',
  '32 languages supported',
  'Enterprise-grade security and SLA',
  'Dedicated account management',
];

export default function AboutPage() {
  const navigate = useNavigate();

  useSEO({
    title: 'About DubLabs — AI Video Dubbing for Global Media Teams',
    description: 'DubLabs breaks language barriers with cutting-edge AI video dubbing. Learn our mission, our technology, and the AWS-backed team building the platform.',
    canonical: 'https://dublabs.ai/about-us',
    breadcrumbs: BREADCRUMBS,
    dateModified: '2026-07-30',
  });

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '100vh' }}>
      <Breadcrumbs items={BREADCRUMBS} />

      {/* Hero */}
      <Box sx={{ py: { xs: 10, md: 18 }, px: { xs: 2, md: 4 }, textAlign: 'center' }}>
        <Box
          component={motion.div}
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          variants={fadeUp}
        >
          <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', mb: 2 }}>
            About DubLabs
          </Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.8rem' }, fontWeight: 800, letterSpacing: '-0.035em', color: '#FFFFFF', fontFamily: 'Inter, sans-serif', mb: 2, maxWidth: '800px', mx: 'auto', lineHeight: 1.1 }}>
            Breaking language barriers with AI-powered dubbing
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '1.1rem', fontFamily: 'Inter, sans-serif', maxWidth: '580px', mx: 'auto', lineHeight: 1.65 }}>
            In today's global media landscape, content knows no borders — but language still limits reach.
            DubLabs gives media professionals the tools to go global without compromise.
          </Typography>
        </Box>
      </Box>

      {/* Mission */}
      <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 10, md: 16 } }}>
        <Box sx={{ maxWidth: '900px', mx: 'auto' }}>
          <Box
            component={motion.div}
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            sx={{ p: { xs: 4, md: 6 }, borderRadius: '20px', backgroundColor: '#0A0A0A', border: '1px solid rgba(255,255,255,0.08)', textAlign: 'center', position: 'relative', overflow: 'hidden',
              '&::before': { content: '""', position: 'absolute', top: 0, left: '20%', right: '20%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)' },
            }}
          >
            <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', mb: 3 }}>
              Our Mission
            </Typography>
            <Typography sx={{ color: '#FFFFFF', fontSize: { xs: '1.1rem', md: '1.3rem' }, fontFamily: 'Inter, sans-serif', lineHeight: 1.75, fontWeight: 400 }}>
              To empower media professionals and organisations to seamlessly transform their video content into any language —
              maintaining perfect lip-sync and natural voice quality — making global content distribution as simple as uploading a file.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Technology */}
      <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 10, md: 16 } }}>
        <Box sx={{ maxWidth: '1100px', mx: 'auto' }}>
          <Box
            component={motion.div}
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            sx={{ textAlign: 'center', mb: 8 }}
          >
            <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', mb: 2 }}>
              Our Technology
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 800, letterSpacing: '-0.03em', color: '#FFFFFF', fontFamily: 'Inter, sans-serif', mb: 1.5 }}>
              A complete dubbing pipeline
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '1.05rem', fontFamily: 'Inter, sans-serif', maxWidth: '500px', mx: 'auto' }}>
              Purpose-built AI infrastructure for broadcast-quality localisation at scale.
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            {TECH_PILLARS.map(({ Icon, title, body }, i) => (
              <Box
                key={title}
                component={motion.div}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                custom={i} variants={fadeUp}
                sx={{ p: 3, borderRadius: '12px', backgroundColor: '#0A0A0A', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                  <Icon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.6)' }} />
                </Box>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '14px', fontFamily: 'Inter, sans-serif', mb: 1, letterSpacing: '-0.01em' }}>
                  {title}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', fontFamily: 'Inter, sans-serif', lineHeight: 1.6 }}>
                  {body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Who We Serve */}
      <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 10, md: 16 } }}>
        <Box sx={{ maxWidth: '1100px', mx: 'auto' }}>
          <Box
            component={motion.div}
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            sx={{ textAlign: 'center', mb: 8 }}
          >
            <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', mb: 2 }}>
              Who We Serve
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 800, letterSpacing: '-0.03em', color: '#FFFFFF', fontFamily: 'Inter, sans-serif' }}>
              Built for every scale of content
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
            {WHO_WE_SERVE.map(({ title, body }, i) => (
              <Box
                key={title}
                component={motion.div}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                custom={i} variants={fadeUp}
                sx={{ p: 3.5, borderRadius: '12px', backgroundColor: '#0A0A0A', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '15px', fontFamily: 'Inter, sans-serif', mb: 1, letterSpacing: '-0.01em' }}>
                  {title}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '14px', fontFamily: 'Inter, sans-serif', lineHeight: 1.65 }}>
                  {body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Why Choose Us */}
      <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 10, md: 16 } }}>
        <Box sx={{ maxWidth: '900px', mx: 'auto' }}>
          <Box
            component={motion.div}
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 6, md: 10 }, alignItems: 'center' }}>
              <Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', mb: 2 }}>
                  Why DubLabs
                </Typography>
                <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, fontWeight: 800, letterSpacing: '-0.03em', color: '#FFFFFF', fontFamily: 'Inter, sans-serif', mb: 2 }}>
                  The standard for AI dubbing
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '1rem', fontFamily: 'Inter, sans-serif', lineHeight: 1.65 }}>
                  We've combined the best AI models with a production-hardened pipeline to deliver results that are indistinguishable from native dubbing.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {WHY_POINTS.map((pt) => (
                  <Box key={pt} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircleIcon sx={{ fontSize: 15, color: '#22C55E', flexShrink: 0 }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', fontFamily: 'Inter, sans-serif' }}>{pt}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Founder */}
      <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 10, md: 16 } }}>
        <Box sx={{ maxWidth: '900px', mx: 'auto' }}>
          <Box
            component={motion.div}
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            sx={{ p: { xs: 3.5, md: 5 }, borderRadius: '20px', backgroundColor: '#0A0A0A', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', mb: 4 }}>
              From the Founder
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 4, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
              <Avatar
                src="/images/founder.jpg"
                alt="Ritesh Vajariya — Founder & CEO of DubLabs"
                imgProps={{ loading: 'lazy', decoding: 'async' }}
                sx={{ width: 72, height: 72, backgroundColor: '#222', fontSize: '22px', fontWeight: 700, border: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}
              >
                RV
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '17px', fontFamily: 'Inter, sans-serif' }}>
                    Ritesh Vajariya
                  </Typography>
                  <IconButton
                    href="https://www.linkedin.com/in/vajariya"
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ color: 'rgba(255,255,255,0.3)', p: 0.5, '&:hover': { color: '#0A66C2' } }}
                  >
                    <LinkedInIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>
                  Founder & CEO, DubLabs · Former AWS AI Leader · Trained thousands of professionals in AI
                </Typography>
              </Box>
            </Box>

            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.05rem', fontFamily: 'Inter, sans-serif', lineHeight: 1.75, fontStyle: 'italic', mb: 3 }}>
              "After years of training professionals in AI through AI Guru and building enterprise AI infrastructure at AWS,
              I've seen firsthand how language barriers limit amazing content from reaching global audiences. With DubLabs,
              we're not just translating words — we're preserving the emotion, timing, and authenticity that makes content powerful."
            </Typography>

            <Box sx={{ p: 3, borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '14px', fontFamily: 'Inter, sans-serif', lineHeight: 1.7 }}>
                Our vision is simple: make every piece of video content accessible to anyone, anywhere, in their native language.
                Whether you're a major media house or an independent creator, DubLabs gives you the tools to go global without compromise.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* CTA */}
      <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 10, md: 16 } }}>
        <Box sx={{ maxWidth: '680px', mx: 'auto' }}>
          <Box
            component={motion.div}
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            sx={{
              textAlign: 'center', p: { xs: 5, md: 8 }, borderRadius: '20px',
              backgroundColor: '#0A0A0A', border: '1px solid rgba(255,255,255,0.07)',
              position: 'relative', overflow: 'hidden',
              '&::before': { content: '""', position: 'absolute', top: 0, left: '25%', right: '25%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)' },
            }}
          >
            <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.2rem' }, fontWeight: 800, letterSpacing: '-0.03em', color: '#FFFFFF', fontFamily: 'Inter, sans-serif', mb: 2 }}>
              Ready to go global?
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '1rem', fontFamily: 'Inter, sans-serif', mb: 5, maxWidth: '380px', mx: 'auto', lineHeight: 1.65 }}>
              Contact our team to learn how DubLabs can scale your content across every language and market.
            </Typography>
            <Button
              component={motion.button}
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/contact')}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
              sx={{ backgroundColor: '#FFFFFF', color: '#000000', fontWeight: 700, fontSize: '15px', px: 4, py: 1.5, borderRadius: '8px', textTransform: 'none', fontFamily: 'Inter, sans-serif', '&:hover': { backgroundColor: '#F0F0F0' } }}
            >
              Get in Touch
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
